import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import "@/App.css";
import { Header } from "@/components/Header";
import Player from "@/pages/Player";
import History from "@/pages/History";
import Auth from "@/pages/Auth";
import Profile from "@/pages/Profile";
import ResetPassword from "@/pages/ResetPassword";
import Saves from "@/pages/Saves";
import FolderDetail from "@/pages/FolderDetail";
import Admin from "@/pages/Admin";
import { AuthCallback } from "@/components/AuthCallback";
import { AuthProvider, useAuth } from "@/auth";
import { addVideo } from "@/lib/api";

const FullScreenMessage = ({ text }) => (
  <div className="min-h-screen bg-black flex items-center justify-center">
    <span className="font-meta text-xs uppercase tracking-[0.3em] text-zinc-600">{text}</span>
  </div>
);

const Protected = ({ children }) => {
  const { user } = useAuth();
  if (user === null) return <FullScreenMessage text="Loading the screening room…" />;
  if (user === false) return <Navigate to="/login" replace />;
  return children;
};

function Shell() {
  const location = useLocation();
  const { user } = useAuth();
  const [video, setVideo] = useState(null);
  const [theater, setTheater] = useState(false);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09 });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  // OAuth callback must be detected during render, before Protected routes run
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  const play = async (url) => {
    const v = await addVideo(url);
    setVideo(v);
    return v;
  };

  const authed = !!user;

  return (
    <>
      <Toaster theme="dark" position="bottom-center" toastOptions={{ style: { background: "#0A0A0A", border: "1px solid rgba(255,255,255,0.1)", color: "#fff" } }} />
      <div className="min-h-screen bg-black text-white">
        {authed && <Header onPlay={play} theater={theater} />}
        <main className={authed ? "min-h-screen pt-20 flex flex-col" : "min-h-screen flex flex-col"}>
          <Routes>
            <Route path="/login" element={<Auth />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/" element={<Protected><Player video={video} setVideo={setVideo} theater={theater} setTheater={setTheater} /></Protected>} />
            <Route path="/history" element={<Protected><History /></Protected>} />
            <Route path="/saves" element={<Protected><Saves /></Protected>} />
            <Route path="/saves/:folderId" element={<Protected><FolderDetail /></Protected>} />
            <Route path="/profile" element={<Protected><Profile /></Protected>} />
            <Route path="/admin" element={<Admin />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Shell />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
